<!-- dialogs/crawler-config-dialog/crawler-config-dialog.component.html -->

<div class="dialog-container p-20" style="overflow: scroll !important">
  <div class="dialog-header fx-row align-items-center justify-content-between mb-16">
    <h2>{{ 'INTEGRATIONS.CRAWLER_CONFIG' | translate }}</h2>
    <button mat-icon-button (click)="onClose()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-divider class="mb-16"></mat-divider>

  <!-- General Info -->
  <div class="section mb-16">
    <h3 class="section-title">{{ 'INTEGRATIONS.GENERAL_INFO' | translate }}</h3>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.NAME' | translate }}:</span>
        <span class="value">{{ integration().name }}</span>
      </div>
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.STATUS' | translate }}:</span>
        <app-crawl-status [status]="crawler().progress.status"></app-crawl-status>
      </div>
      @if (excelSpec(); as spec) {
        <div class="info-item">
          <span class="label">{{ 'INTEGRATIONS.EXCEL_FILE' | translate }}:</span>
          <span class="value">{{ spec.excelFileName }}</span>
        </div>
        <div class="info-item">
          <span class="label">{{ 'INTEGRATIONS.URL_COLUMN' | translate }}:</span>
          <span class="value">{{ spec.urlColumn }}</span>
        </div>
      }
    </div>
  </div>

  <mat-divider class="mb-16"></mat-divider>

  <!-- Progress Info -->
  <div class="section mb-16">
    <h3 class="section-title">{{ 'INTEGRATIONS.PROGRESS_INFO' | translate }}</h3>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.TOTAL_URLS' | translate }}:</span>
        <span class="value">{{ crawler().progress.total }}</span>
      </div>
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.PROCESSED' | translate }}:</span>
        <span class="value">{{ crawler().progress.processed }}</span>
      </div>
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.FAILED' | translate }}:</span>
        @if (crawler().progress.failed > 0) {
          <span class="value error">{{ crawler().progress.failed }}</span>
        } @else {
          <span class="value">0</span>
        }
      </div>
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.CURRENT_BATCH' | translate }}:</span>
        <span class="value">
          {{ crawler().progress.currentBatch ?? 0 }} / {{ crawler().progress.totalBatches ?? 0 }}
        </span>
      </div>
      @if (crawler().progress.startedAt) {
        <div class="info-item">
          <span class="label">{{ 'INTEGRATIONS.STARTED_AT' | translate }}:</span>
          <span class="value">{{ crawler().progress.startedAt | date: 'medium' }}</span>
        </div>
      }
      @if (crawler().progress.completedAt) {
        <div class="info-item">
          <span class="label">{{ 'INTEGRATIONS.COMPLETED_AT' | translate }}:</span>
          <span class="value">{{ crawler().progress.completedAt | date: 'medium' }}</span>
        </div>
      }
    </div>
  </div>

  <mat-divider class="mb-16"></mat-divider>

  <!-- Screenshot Settings -->
  <div class="section mb-16">
    <h3 class="section-title">{{ 'INTEGRATIONS.SCREENSHOT_SETTINGS' | translate }}</h3>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.FORMAT' | translate }}:</span>
        <span class="value">{{ crawler().config.screenshotFormat.toUpperCase() }}</span>
      </div>
      @if (crawler().config.screenshotFormat === 'jpeg') {
        <div class="info-item">
          <span class="label">{{ 'INTEGRATIONS.QUALITY' | translate }}:</span>
          <span class="value">{{ crawler().config.quality }}%</span>
        </div>
      }
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.VIEWPORT' | translate }}:</span>
        <span class="value">
          {{ crawler().config.viewport.width }}x{{ crawler().config.viewport.height }}
        </span>
      </div>
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.CONCURRENCY' | translate }}:</span>
        <span class="value">{{ crawler().config.concurrency }} {{ 'INTEGRATIONS.WORKERS' | translate }}</span>
      </div>
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.DELAY' | translate }}:</span>
        <span class="value">{{ crawler().config.delay }}ms</span>
      </div>
      <div class="info-item">
        <span class="label">{{ 'INTEGRATIONS.BATCH_SIZE' | translate }}:</span>
        <span class="value">{{ crawler().config.batchSize }} {{ 'INTEGRATIONS.URLS_PER_BATCH' | translate }}</span>
      </div>
      @if (crawler().config.pageTimeout; as pageTimeout) {
        <div class="info-item">
          <span class="label">{{ 'INTEGRATIONS.PAGE_TIMEOUT' | translate }}:</span>
          <span class="value">{{ pageTimeout }}ms</span>
        </div>
      }
    </div>
  </div>

  <!-- Output Info (if completed) -->
  @if (output(); as out) {
    <mat-divider class="mb-16"></mat-divider>
    <div class="section mb-16">
      <h3 class="section-title">{{ 'INTEGRATIONS.OUTPUT_INFO' | translate }}</h3>
      <div class="info-grid">
        @if (out.totalSize) {
          <div class="info-item">
            <span class="label">{{ 'INTEGRATIONS.TOTAL_SIZE' | translate }}:</span>
            <span class="value">{{ formatBytes(out.totalSize) }}</span>
          </div>
        }
        @if (out.screenshotBlobUrls?.length) {
          <div class="info-item">
            <span class="label">{{ 'INTEGRATIONS.SCREENSHOTS_CREATED' | translate }}:</span>
            <span class="value">{{ out.screenshotBlobUrls?.length ?? 0 }}</span>
          </div>
        }
        @if (out.manifestBlobUrl) {
          <div class="info-item fx-flex-100">
            <span class="label">{{ 'INTEGRATIONS.MANIFEST' | translate }}:</span>
            <a [href]="out.manifestBlobUrl" target="_blank" class="value link">
              {{ 'INTEGRATIONS.DOWNLOAD_MANIFEST' | translate }}
            </a>
          </div>
        }
      </div>
    </div>
  }

  <!-- Error Message (if failed) -->
  @if (crawler().progress.errorMessage; as errorMessage) {
    <mat-divider class="mb-16"></mat-divider>
    <div class="section mb-16">
      <h3 class="section-title error">
        <mat-icon class="mr-8">error</mat-icon>
        {{ 'INTEGRATIONS.ERROR_MESSAGE' | translate }}
      </h3>
      <div class="error-container">
        <pre class="error-message">{{ errorMessage }}</pre>
      </div>
    </div>
  }

  <mat-divider class="mt-16 mb-16"></mat-divider>

  <div class="dialog-actions fx-row justify-content-end">
    <button class="main-button" (click)="onClose()">
      {{ 'ACTIONS.CLOSE' | translate }}
    </button>
  </div>
</div>
